import { useExperiment } from '@m/hooks/data/experiments';

import type {
  OnboardingExperimentCohort,
  SignInOnboardingExperimentCohort,
} from '../onboardingExperimentCohort';
import type { EvaluationContext } from '@m/services/experimentation/EvaluationContext';

export const WEB_EXPRESS_ONBOARDING_EXPERIMENT_NAME_BY_COHORT = {
  agnostic: 'web_express_onboarding_agnostic',
  enterprise: 'web_express_onboarding_enterprise',
  individual: 'web_express_onboarding_individual',
  stp: 'web_express_onboarding_stp',
} satisfies Record<OnboardingExperimentCohort, string>;

const WEB_EXPRESS_ONBOARDING_EVALUATION_CONTEXT_BY_COHORT = {
  agnostic: {
    customAttributes: {
      flowType: 'agnostic',
    },
  },
  enterprise: {
    customAttributes: {
      flowType: 'enterprise',
    },
  },
  individual: {
    customAttributes: {
      flowType: 'individual',
    },
  },
  stp: {
    customAttributes: {
      flowType: 'stp',
    },
  },
} satisfies Record<OnboardingExperimentCohort, EvaluationContext>;

type SignInOnboardingExperimentGroup = 'control' | 'treatment';

export type SignInOnboardingExperimentState = {
  isFallback?: boolean;
} & ({ status: 'inactive' } | { status: 'pending' } | { status: SignInOnboardingExperimentGroup });

function useOnboardingExperiment(
  cohort: OnboardingExperimentCohort,
  enabled: boolean,
  override: SignInOnboardingExperimentGroup | null = null,
): SignInOnboardingExperimentState {
  const { data, isPending } = useExperiment(
    WEB_EXPRESS_ONBOARDING_EXPERIMENT_NAME_BY_COHORT[cohort],
    WEB_EXPRESS_ONBOARDING_EVALUATION_CONTEXT_BY_COHORT[cohort],
    enabled && override === null,
    { staleTime: Infinity },
  );

  if (override) return { status: override };
  if (!enabled && !data) return { status: 'inactive' };
  if (isPending) return { status: 'pending' };
  const isFallback = data?.isFallback === true;
  return {
    status: data?.parameters?.use_new_onboarding === true ? 'treatment' : 'control',
    ...(isFallback ? { isFallback: true } : {}),
  };
}

export function useSignInOnboardingExperiment(
  cohort: SignInOnboardingExperimentCohort,
  enabled = true,
  override: SignInOnboardingExperimentGroup | null = null,
): SignInOnboardingExperimentState {
  return useOnboardingExperiment(cohort, enabled, override);
}

export function useStpOnboardingExperiment(enabled: boolean): SignInOnboardingExperimentState {
  return useOnboardingExperiment('stp', enabled);
}
